import { setTrackingId } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useImperativeHandle,
	useRef
} from "react";
import styles from "./HubOverviewLayout.module.less";
import { ActiveFilter } from "./common";
import { HubBackButton } from "./HubBackButton";
import { PageContentTitle, ToolbarAction } from "client/Components";

export type HubOverviewLayoutProps = {
	trackingId: string;
};

const HubOverviewLayoutInternal = (props: PropsWithChildren<HubOverviewLayoutProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{children}
		</div>
	);
};
HubOverviewLayoutInternal.displayName = "HubOverviewLayout";

const HubOverviewLayoutContent = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.content}>
			{children}
		</div>
	);
};
HubOverviewLayoutContent.displayName = "HubOverviewLayout.Content";

type HubOverviewLayoutSectionContainerProps = {
	trackingId: string;
};

export type HubOverviewLayoutSectionContainerHandle = {
	scrollToTop(): void;
};

const HubOverviewLayoutSectionContainer = forwardRef<HubOverviewLayoutSectionContainerHandle, PropsWithChildren<HubOverviewLayoutSectionContainerProps>>(
	(props: PropsWithChildren<HubOverviewLayoutSectionContainerProps>, ref?: Ref<HubOverviewLayoutSectionContainerHandle>): ReactElement => {
		const {
			trackingId,
			children
		} = props;

		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		useImperativeHandle(
			ref,
			(): HubOverviewLayoutSectionContainerHandle => ({
				scrollToTop: (): void => {
					containerRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		return (
			<div
				{...setTrackingId(trackingId)}
				className={styles.sectionContainer}
				ref={containerRef}
			>
				{children}
			</div>
		);
	}
);
HubOverviewLayoutSectionContainer.displayName = "HubOverviewLayout.SectionContainer";

type HubOverviewLayoutSectionProps = {
	trackingId: string;
};

const HubOverviewLayoutSection = (props: PropsWithChildren<HubOverviewLayoutSectionProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.section}
		>
			{children}
		</div>
	);
};
HubOverviewLayoutSection.displayName = "HubOverviewLayout.Section";

type HubOverviewLayoutSectionTitleProps = {
	trackingId: string;
};

const HubOverviewLayoutSectionTitle = (props: PropsWithChildren<HubOverviewLayoutSectionTitleProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<h3
			{...setTrackingId(trackingId)}
			className={styles.sectionTitle}
		>
			{children}
		</h3>
	);
};
HubOverviewLayoutSectionTitle.displayName = "HubOverviewLayout.SectionTitle";

type HubOverviewLayoutHeaderProps<TActionContext> = {
	activeFilter: ActiveFilter | null;
	onClearActiveFilter: () => void;
	title: string | undefined;
	trackingId: string;
} & ({
	actions: readonly ToolbarAction<TActionContext>[];
	actionContext: TActionContext;
} | {
	actions?: undefined;
	actionContext?: undefined;
});

const HubOverviewLayoutHeader = <TActionContext, >(
	props: PropsWithChildren<HubOverviewLayoutHeaderProps<TActionContext>>
): ReactElement => {
	const {
		activeFilter,
		onClearActiveFilter,
		title,
		trackingId,
		children
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.header}
		>
			{activeFilter && (
				<HubBackButton
					activeFilter={activeFilter}
					onBack={onClearActiveFilter}
				/>
			)}

			<PageContentTitle
				title={title ?? ""}
				{...(props.actions
					? {
						actions: props.actions,
						actionContext: props.actionContext
					}
					: {
						actions: undefined
					}
				)}
			/>

			<div className={styles.searchAndSortOptions}>
				{children}
			</div>
		</div>
	);
};
HubOverviewLayoutHeader.displayName = "HubOverviewLayout.Header";

const HubOverviewLayoutMobilePlusMenuSpacing = (): ReactElement => {
	return (
		<div className="preserve-space-for-mobile-plus-button"/>
	);
};
HubOverviewLayoutMobilePlusMenuSpacing.displayName = "HubOverviewLayout.MobilePlusMenuSpacing";

const HubOverviewLayoutBreadcrumb = (props: PropsWithChildren<{}>): ReactElement => {
	const { children } = props;

	return (
		<div className={styles.breadcrumb}>
			{children}
		</div>
	);
};
HubOverviewLayoutBreadcrumb.displayName = "HubOverviewLayout.Breadcrumb";

type HubOverviewLayoutWithSubComponents = typeof HubOverviewLayoutInternal & {
	Content: typeof HubOverviewLayoutContent;
	Header: typeof HubOverviewLayoutHeader;
	SectionContainer: typeof HubOverviewLayoutSectionContainer;
	Section: typeof HubOverviewLayoutSection;
	SectionTitle: typeof HubOverviewLayoutSectionTitle;
	MobilePlusMenuSpacing: typeof HubOverviewLayoutMobilePlusMenuSpacing;
	Breadcrumb: typeof HubOverviewLayoutBreadcrumb;
};

export const HubOverviewLayout = HubOverviewLayoutInternal as unknown as HubOverviewLayoutWithSubComponents;

HubOverviewLayout.Content = HubOverviewLayoutContent;
HubOverviewLayout.Header = HubOverviewLayoutHeader;
HubOverviewLayout.SectionContainer = HubOverviewLayoutSectionContainer;
HubOverviewLayout.Section = HubOverviewLayoutSection;
HubOverviewLayout.SectionTitle = HubOverviewLayoutSectionTitle;
HubOverviewLayout.MobilePlusMenuSpacing = HubOverviewLayoutMobilePlusMenuSpacing;
HubOverviewLayout.Breadcrumb = HubOverviewLayoutBreadcrumb;